import { RecurringOrderDetailComponent } from './recurring-orders/recurring-order-detail/recurring-order-detail.component';
import { RecurringOrderCreateComponent } from './recurring-orders/recurring-order-create/recurring-order-create.component';
import { ArticlesComponent } from './billing/articles/articles.component';
import { ExportListComponent } from './billing/export-list/export-list.component';
import { SchedulesComponent } from './billing/schedules/schedules.component';
import { ContactsResolver } from './shared/resolvers/contacts.resolver';
import { TimeTrackingComponent } from './time-tracking/time-tracking.component';
import { MissionsListComponent } from './missions/missions-list/missions-list.component';
import { ContainersComponent } from './repository/containers/containers.component';
import { ProductTypesComponent } from './repository/product-types/product-types.component';
import { AdvancedSearchComponent } from './advanced-search/advanced-search.component';
import { TransportTypesComponent } from './repository/transport-types/transport-types.component';
import { ClientsComponent } from './repository/clients/clients.component';
import { AddressesPageComponent } from './repository/addresses-page/addresses-page.component';
import { SitesPageComponent } from './repository/sites-page/sites-page.component';
import { FormContactComponent } from './form-contact/form-contact.component';
import { OrderCreateComponent } from './orders/order-create/order-create.component';
import { OrderTransmissionEditComponent } from './orders/order-transmission-edit/order-transmission-edit.component';
import { OrdersComponent } from './orders/orders.component';
import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { UnauthGuard } from './shared/guards/unauth.guard';
import { AuthGuard } from './shared/guards/auth.guard';
import { OrderCreateModeGuard } from './shared/guards/order-create-mode.guard';

import { LoginComponent } from './auth/login/login.component';
import { OrdersListComponent } from './orders/orders-list/orders-list.component';

import { OrderResolver } from './shared/resolvers/order.resolver';
import { UsersComponent } from './users/users.component';
import { RoleName } from '@enums/role-name.enum';
import { UsersResolver } from './shared/resolvers/users.resolver';
import { ForgotPasswordComponent } from './auth/forgot-password/forgot-password.component';
import { ChangePasswordComponent } from './auth/change-password/change-password.component';
import { ChangePwdGuard } from './shared/guards/change-pwd.guard';
import { ErrorComponent } from './error/error.component';
import { OrderDetailComponent } from './orders/order-detail/order-detail.component';
import { RepositoryComponent } from './repository/repository.component';
import { SitesResolver } from './shared/resolvers/sites.resolver';
import { SiteGroupsResolver } from './shared/resolvers/site-groups.resolver';
import { AddressesResolver } from './shared/resolvers/addresses.resolver';
import { AddressGroupsResolver } from './shared/resolvers/address-groups.resolver';
import { ClientsResolver } from './shared/resolvers/clients.resolver';
import { TransportTypesResolver } from './shared/resolvers/transport-types.resolver';
import { UrgencyLevelsResolver } from './shared/resolvers/urgency-levels.resolver';
import { VehiclesResolver } from './shared/resolvers/vehicles/vehicles.resolver';
import { ProductTypesResolver } from './shared/resolvers/product-types.resolver';
import { ContainersResolver } from './shared/resolvers/containers.resolver';
import { MissionsComponent } from './missions/missions.component';
import { BillingComponent } from './billing/billing.component';
import { HolidayGroupsResolver } from './shared/resolvers/holiday-groups.resolver';
import { HolidaysComponent } from './billing/holidays/holidays.component';
import { ContactsComponent } from './contacts/contacts.component';
import { HolidayGroupsComponent } from './billing/holiday-groups/holiday-groups.component';
import { SchedulesResolver } from './shared/resolvers/schedules.resolver';
import { RecurringOrdersComponent } from './recurring-orders/recurring-orders.component';
import { RecurringOrdersListComponent } from './recurring-orders/recurring-orders-list/recurring-orders-list.component';
import { RecurringOrderResolver } from './shared/resolvers/recurring-order.resolver';
import { WaitingTimesComponent } from './billing/waiting-times/waiting-times.component';
import { WaitingTimesResolver } from './shared/resolvers/waiting-times.resolver';
import { AlreadyLoggedInGuard } from './shared/guards/already-logged-in.guard';

const routes: Routes = [
  {
    path: 'login',
    component: LoginComponent,
    canActivate: [UnauthGuard, AlreadyLoggedInGuard],
    data: { title: 'auth.login.title' },
  },
  {
    path: 'login/forgot-password',
    component: ForgotPasswordComponent,
    canActivate: [UnauthGuard],
    data: { title: 'auth.forgotPassword' },
  },
  {
    path: 'change-password',
    component: ChangePasswordComponent,
    canActivate: [ChangePwdGuard],
    data: { title: 'auth.changePassword.title' },
  },
  {
    path: 'welcome',
    component: ChangePasswordComponent,
    canActivate: [ChangePwdGuard],
    data: { title: 'auth.changePassword.title' },
  },
  {
    path: 'history',
    component: MissionsComponent,
    canActivateChild: [AuthGuard],
    data: {
      title: 'missions.title',
    },
    children: [
      {
        path: 'list',
        component: MissionsListComponent,
        data: {
          title: 'missions.list.title',
          module: 'Dispatch',
        },
      },
      { path: '**', redirectTo: 'list', pathMatch: 'full' },
    ],
  },
  {
    path: 'orders',
    component: OrdersComponent,
    canActivateChild: [AuthGuard],
    data: {
      title: 'orders.title',
      titleForClient: 'orders.titleForClient',
      checkRoleClient: true,
    },
    children: [
      {
        path: 'create',
        component: OrderCreateComponent,
        canActivate: [OrderCreateModeGuard],
        data: {
          title: 'orders.create.title',
          mode: 'transport',
          roles: [
            RoleName.admin,
            RoleName.admin_subcontractor,
            RoleName.super_admin,
            RoleName.bizzdev,
            RoleName.client,
            RoleName.admin_client,
            RoleName.dispatcher,
          ],
          module: 'Dispatch',
        },
      },
      {
        path: 'transmission',
        data: {},
        children: [
          {
            path: 'create',
            component: OrderTransmissionEditComponent,
            canActivate: [OrderCreateModeGuard],
            data: {
              title: 'orders.createTransmission.title',
              mode: 'transmission',
              formMode: 'create',
              roles: [
                RoleName.admin,
                RoleName.admin_subcontractor,
                RoleName.super_admin,
                RoleName.bizzdev,
                RoleName.client,
                RoleName.admin_client,
                RoleName.dispatcher,
              ],
              module: 'Dispatch',
            },
          }
          ,
        ],
      },
      {
        path: 'list',
        component: OrdersListComponent,
        data: {
          title: 'orders.list.title',
          titleForClient: 'orders.titleForClient',
          module: 'Dispatch',
          checkRoleClient: true,
        },
      },
      {
        path: ':id',
        component: OrderDetailComponent,
        resolve: { order: OrderResolver },
        data: {
          title: 'orders.detail.title',
          module: 'Dispatch',
        },
      },
    ],
  },
  {
    path: 'advancedSearch',
    component: AdvancedSearchComponent,
    canActivate: [AuthGuard],
    data: {
      title: 'advancedSearch.title',
      roles: [
        RoleName.admin,
        RoleName.admin_subcontractor,
        RoleName.super_admin,
        RoleName.bizzdev,
        RoleName.dispatcher,
      ],
      module: 'Dispatch',
    },
  },
  {
    path: 'users',
    component: UsersComponent,
    canActivate: [AuthGuard],
    data: {
      title: 'users.title',
      roles: [
        RoleName.bizzdev,
        RoleName.admin_client,
        RoleName.super_admin,
        RoleName.admin,
        RoleName.admin_subcontractor,
      ],
      module: 'Dispatch',
    },
  },
  {
    path: 'recurring-orders',
    component: RecurringOrdersComponent,
    canActivateChild: [AuthGuard],
    data: {
      title: 'recurringOrders.title',
      roles: [RoleName.admin, RoleName.admin_subcontractor, RoleName.bizzdev, RoleName.super_admin],
      module: 'Dispatch',
    },
    children: [
      {
        path: 'create',
        component: RecurringOrderCreateComponent,
        data: {
          title: 'recurringOrders.create.title',
          module: 'Dispatch',
        },
      },
      {
        path: 'list',
        component: RecurringOrdersListComponent,
        data: {
          title: 'recurringOrders.list.title',
          module: 'Dispatch',
        },
      },
      {
        path: ':id',
        component: RecurringOrderDetailComponent,
        resolve: { order: RecurringOrderResolver },
        data: {
          title: 'recurringOrders.detail.title',
          module: 'Dispatch',
        },
      },
      { path: '**', redirectTo: 'list', pathMatch: 'full' },
    ],
  },
  {
    path: 'billing',
    component: BillingComponent,
    canActivateChild: [AuthGuard],
    data: {
      title: 'billing.title',
      roles: [RoleName.admin, RoleName.admin_subcontractor, RoleName.bizzdev, RoleName.super_admin],
      module: 'Facturation',
    },
    children: [
      {
        path: 'holidays',
        component: HolidaysComponent,
        resolve: { holidayGroups: HolidayGroupsResolver },
        data: {
          title: 'billing.holidays.title',
          roles: [RoleName.admin, RoleName.admin_subcontractor, RoleName.bizzdev, RoleName.super_admin],
        },
      },
      {
        path: 'holiday-groups',
        component: HolidayGroupsComponent,
        resolve: { holidayGroups: HolidayGroupsResolver },
        data: {
          title: 'billing.holidayGroups.title',
          roles: [RoleName.admin, RoleName.admin_subcontractor, RoleName.bizzdev, RoleName.super_admin],
        },
      },
      {
        path: 'schedules',
        component: SchedulesComponent,
        resolve: { schedules: SchedulesResolver },
        data: {
          title: 'billing.schedules.title',
          roles: [RoleName.admin, RoleName.admin_subcontractor, RoleName.bizzdev, RoleName.super_admin],
        },
      },
      {
        path: 'articles',
        component: ArticlesComponent,
        resolve: { clients: ClientsResolver },
        data: {
          title: 'billing.articles.title',
          roles: [RoleName.admin, RoleName.admin_subcontractor, RoleName.bizzdev, RoleName.super_admin],
        },
      },
      {
        path: 'export-list',
        component: ExportListComponent,
        data: {
          title: 'billing.exportList.title',
          roles: [RoleName.admin, RoleName.admin_subcontractor, RoleName.bizzdev, RoleName.super_admin],
        },
      },
      {
        path: 'waiting-times',
        component: WaitingTimesComponent,
        resolve: { waitingTimes: WaitingTimesResolver },
        data: {
          title: 'billing.waitingTimes.title',
          roles: [RoleName.admin, RoleName.admin_subcontractor, RoleName.bizzdev, RoleName.super_admin],
        },
      },
      { path: '**', redirectTo: 'holidays', pathMatch: 'full' },
    ],
  },
  {
    path: 'repository',
    component: RepositoryComponent,
    canActivateChild: [AuthGuard],
    data: {
      title: 'repository.title',
      roles: [RoleName.admin, RoleName.admin_subcontractor, RoleName.bizzdev, RoleName.super_admin],
      module: 'Dispatch',
    },
    children: [
      {
        path: 'sites',
        component: SitesPageComponent,
        resolve: { sites: SitesResolver, siteGroups: SiteGroupsResolver },
        data: {
          title: 'repository.sites.title',
          roles: [RoleName.admin, RoleName.admin_subcontractor, RoleName.bizzdev, RoleName.super_admin],
        },
      },
      {
        path: 'addresses',
        component: AddressesPageComponent,
        resolve: {
          addresses: AddressesResolver,
          addressGroups: AddressGroupsResolver,
          sites: SitesResolver,
          siteGroups: SiteGroupsResolver,
        },
        data: {
          title: 'repository.addresses.title',
          roles: [RoleName.admin, RoleName.admin_subcontractor, RoleName.bizzdev, RoleName.super_admin],
        },
      },
      {
        path: 'clients',
        component: ClientsComponent,
        resolve: { clients: ClientsResolver },
        data: {
          title: 'repository.clients.title',
          roles: [RoleName.bizzdev, RoleName.super_admin, RoleName.admin, RoleName.admin_subcontractor],
        },
      },
      {
        path: 'containers',
        component: ContainersComponent,
        resolve: { containers: ContainersResolver },
        data: {
          title: 'repository.containers.title',
          roles: [RoleName.admin, RoleName.admin_subcontractor, RoleName.bizzdev, RoleName.super_admin],
        },
      },
      {
        path: 'productTypes',
        component: ProductTypesComponent,
        resolve: { productTypes: ProductTypesResolver },
        data: {
          title: 'repository.productTypes.title',
          roles: [RoleName.admin, RoleName.admin_subcontractor, RoleName.bizzdev, RoleName.super_admin],
        },
      },
      {
        path: 'categories',
        component: TransportTypesComponent,
        resolve: {
          transportTypes: TransportTypesResolver,
          urgencyLevels: UrgencyLevelsResolver,
        },
        data: {
          title: 'repository.categories.title',
          roles: [RoleName.bizzdev, RoleName.super_admin, RoleName.admin, RoleName.admin_subcontractor],
        },
      },
      { path: '**', redirectTo: 'sites', pathMatch: 'full' },
    ],
  },
  {
    path: 'contact',
    component: FormContactComponent,
    canActivate: [AuthGuard],
    resolve: { clients: ClientsResolver },
    data: {
      title: 'contact.title',
    },
  },
  {
    path: 'contacts',
    component: ContactsComponent,
    canActivate: [AuthGuard],
    resolve: { contacts: ContactsResolver },
    data: {
      title: 'contacts.title',
      module: 'Contact',
      roles: [
        RoleName.bizzdev,
        RoleName.super_admin,
        RoleName.admin,
        RoleName.admin_subcontractor,
        RoleName.dispatcher,
      ],
    },
  },
  {
    path: 'documents',
    loadChildren: () =>
      import('./documents/documents.module').then((m) => m.DocumentsModule),
    canActivate: [AuthGuard],
    data: {
      title: 'documents.title',
      module: 'Dispatch',
    },
  },
  {
    path: 'messages',
    loadChildren: () =>
      import('./messages/messages.module').then((m) => m.MessagesModule),
    canActivate: [AuthGuard],
    data: {
      title: 'messages.title',
      module: 'Dispatch',
      roles: [RoleName.admin, RoleName.admin_subcontractor, RoleName.bizzdev, RoleName.super_admin],
    },
  },
  {
    path: 'vehicles',
    loadChildren: () =>
      import('./vehicles/vehicles.module').then((m) => m.VehiclesModule),
    data: {
      title: 'vehicles.title',
      roles: [RoleName.admin, RoleName.bizzdev, RoleName.super_admin],
    },
  },
  {
    path: 'timeTracking',
    component: TimeTrackingComponent,
    canActivate: [AuthGuard],
    data: {
      title: 'timeTracking.title',
      module: 'Pointage',
      roles: [
        RoleName.bizzdev,
        RoleName.super_admin,
        RoleName.admin,
        RoleName.dispatcher,
        RoleName.driver,
      ],
    },
  },
  { path: 'error', component: ErrorComponent, data: { title: 'error' } },
  { path: '**', redirectTo: 'orders/list', pathMatch: 'full' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {})],
  exports: [RouterModule],
})
export class AppRoutingModule {}
